import { useContext, useEffect } from "react";
import { PageTourContext, type PageTourControls } from "./context";
import type { CustomPageTourDefinition, PageTourDefinition } from "./types";

const noop = () => {};
const inactive: PageTourControls = {
  open: false,
  pending: false,
  start: noop,
  close: noop,
};

/** Pass a stable definition; readiness should exclude loading/error screens. */
export function usePageTour(tour: PageTourDefinition, ready = true) {
  const context = useContext(PageTourContext);
  const register = context?.register;
  const pathname = context?.pathname ?? "";
  useEffect(() => {
    if (!ready || !register) return;
    return register({ ...tour, kind: "spotlight" }, pathname);
  }, [tour, ready, register, pathname]);

  return ready && context?.tourId === tour.id ? context : inactive;
}

/** Coordinates an existing custom renderer (the transcript editor) with news. */
export function useCustomPageTour(
  tour: CustomPageTourDefinition,
  ready = true
) {
  const context = useContext(PageTourContext);
  const register = context?.register;
  const pathname = context?.pathname ?? "";
  useEffect(() => {
    if (!ready || !register) return;
    return register({ ...tour, kind: "custom" }, pathname);
  }, [tour, ready, register, pathname]);

  if (ready && context?.tourId === tour.id) return context;
  // Defer page-owned first-run prompts during the registration commit too.
  return { ...inactive, pending: ready && !!context };
}

export function usePageTourPaused() {
  return useContext(PageTourContext)?.paused ?? false;
}

export function usePageTourReplay() {
  const context = useContext(PageTourContext);
  return context?.available ? context.start : undefined;
}
